import * as React from 'react';
import {useCallback, useEffect, useState} from 'react';
import {
    CenterSpinner,
    headerKeys,
    toTypeName,
    translate,
    useApiErrors,
    useAuth,
    useCommandValidation,
    useDomainEvent,
    useEvent,
    useLazyCommand,
    uuidv4
} from '@soap/modules'
import {
    PrimaryActionMenuButton,
    ValidationNotification
} from '@soap/baseweb'
import {CenteredCard} from "../z40-uicomp/centered-card";
import {Frame} from "../z40-uicomp/frame";
import {LabelLarge, LabelMedium, LabelSmall, ParagraphMedium} from "baseui/typography";
import {useParams} from "react-router";
import {Centered, SpaceAround, SpaceBottom, SpaceTop, SpaceTopAndBottom} from "../z40-uicomp/layout";
import {Cell, Grid} from "baseui/layout-grid";
import {useStyletron} from "baseui";
import {Table} from "baseui/table-semantic";
import {Input} from "baseui/input";
import {Select} from "baseui/select";
import {TalkBadge} from "../z40-uicomp/talk-badge";
import {useFormatDisplayDate} from "../utils/useFormatDisplayDate";
import {useDebounce} from "../hooks/useDebounce";
import ErrorModal from "../z40-uicomp/error-modal";
import {useEnsurePaddleSetup} from "../hooks/useEnsurePaddleSetup";

function useCommandValidationWithLogging() {
    const {validateCommand} = useCommandValidation();
    return {
        validateCommand: async (command, schema) => {
            console.log(`validating ${schema ?? command.$type}`, command);
            return await validateCommand(command, schema);
        }
    };
}

export function Order() {

    useEnsurePaddleSetup();
    const {validateCommand} = useCommandValidationWithLogging();

    const toMoney = function (price, currency) {

        if (price === 0) return "Free";
        const amount = Number.isInteger(price) ? price : price.toFixed(2);
        return amount + " " + currency;
    }

    let {id: statefulProcessIdAkaOrderId} = useParams();
    statefulProcessIdAkaOrderId = window.atob(statefulProcessIdAkaOrderId);

    //* styling 
    const [css, theme] = useStyletron();
    const formatDate = useFormatDisplayDate();


    const viewStates = {
        WaitingForOrderData: 0,
        SendingContinueOrder: 11,   // send C169 then transition to WaitingForE140
        WaitingForE140: 12,         // C169 sent, waiting for E140 (or token refresh + retry)
        OrderDataReceivedWaitingForUserAction: 1,
        PaymentFailed: 2,
        PaymentAcceptedButNotConfirmed: 3,
        PaymentConfirmed: 4,
        WaitingForCheckout: 5,
        PendingCurrencyChangePreventedOrder: 6,
        TryingToUpgradeToExistingPlan: 7,
        PaymentNotRequired: 8,
        QuotaResolutionRequired: 9,
        UnarchiveOpportunity: 10
    };

    const [viewState, setViewState] = useState(viewStates.WaitingForOrderData);

    const [firstName, setFirstName] = React.useState('');
    const [lastName, setLastName] = React.useState('');

    const [postcode, setPostcode] = React.useState('');
    const debouncedPostcode = useDebounce(postcode, 500);

    const [countryCode, setCountryCode] = React.useState();
    const [countries, setCountries] = React.useState([]);
    const [currencyCode, setCurrencyCode] = React.useState();
    const [currencies, setCurrencies] = React.useState([]);

    const [orderSummaryData, setOrderSummaryData] = useState();
    const [orderCompletedData, setOrderCompletedData] = useState();
    const [confirmOrderConversationId, setConfirmOrderConversationId] = useState();

    // Track successful checkout completion - checkout.closed fires after checkout.completed when user
    // completes payment, and we must not treat that as "cancelled"
    const checkoutCompletedRef = React.useRef(false);

    //* show if error
    const [notificationMessage, setNotificationMessage] = useState(undefined);

    const [disabled, setDisabled] = useState(true);

    // Quota resolution state
    const [resolutionPanelExpanded, setResolutionPanelExpanded] = useState(false);
    const [resolutionChoices, setResolutionChoices] = useState({});

    // Unarchive state
    const [unarchivePanelExpanded, setUnarchivePanelExpanded] = useState(false);
    const [selectedUnarchiveIds, setSelectedUnarchiveIds] = useState([]);

    //* check a valid plan was selected and create an account on the free plan if one doesn't yet exist,  returns E140
    //* Do NOT pass initialCommand: after Auth0 redirect the JWT may not yet have API permissions (roles).
    //* We send C169 explicitly after refreshing the token so the backend accepts it.
    const [continueOrderTrigger, conversationIds] = useLazyCommand('C169v1_ContinueOrderAfterLogin');

    const [resolveQuotaTrigger] = useLazyCommand('C294v1_ResolveOrderQuota');

    const [unarchiveStudentsTrigger] = useLazyCommand('C295v1_UnarchiveStudentsForOrder');

    const {authReady, isAuthenticated, refresh} = useAuth("order.js");
    const [permissionErrorRetryCount, setPermissionErrorRetryCount] = useState(0);

    // When auth is ready and we're still waiting for order data, kick off the C169 flow (sends C169, then we wait for E140 or token refresh).
    useEffect(() => {
        if (!authReady || !isAuthenticated || viewState !== viewStates.WaitingForOrderData) return;
        setViewState(viewStates.SendingContinueOrder);
    }, [authReady, isAuthenticated, viewState]);

    // When we enter SendingContinueOrder, send C169 then wait for E140. useTokenRefreshRequired or permission-error handler set this state again to retry.
    useEffect(() => {
        if (viewState !== viewStates.SendingContinueOrder) return;
        continueOrderTrigger({
            headers: [{key: headerKeys.statefulProcessId, value: statefulProcessIdAkaOrderId}]
        });
        setViewState(viewStates.WaitingForE140);
    }, [viewState, statefulProcessIdAkaOrderId, continueOrderTrigger]);

    useEffect(() => {
        if (viewState == viewStates.OrderDataReceivedWaitingForUserAction && postcode !== orderSummaryData.billingPostcode) {
            //* in the US this can change the tax you pay e.g. Texas vs California

            sendConfirmCommand(true, currencyCode, countryCode, debouncedPostcode, firstName, lastName);
        }
    }, [debouncedPostcode]);

    // Initialize resolution choices when quota resolution screen is shown
    useEffect(() => {
        if (viewState === viewStates.QuotaResolutionRequired && orderSummaryData?.quotaResolutionStudents) {
            const students = orderSummaryData.quotaResolutionStudents || [];
            if (students.length > 0 && Object.keys(resolutionChoices).length === 0) {
                const initialChoices = {};
                students.forEach(s => {
                    initialChoices[s.e140_StudentId] = {
                        archive: false,
                        removeLogin: false
                    };
                });
                setResolutionChoices(initialChoices);
            }
        }
    }, [viewState, orderSummaryData?.quotaResolutionStudents]);

    useDomainEvent({
        eventName: "checkout-completed",
        onEventReceived: () => {
            checkoutCompletedRef.current = true;
            Paddle.Checkout.close();
            setViewState(viewStates.PaymentAcceptedButNotConfirmed);
        }
    });

    useDomainEvent({
        eventName: "checkout-closed",
        onEventReceived: () => {
            // checkout.closed fires after checkout.completed when user completes payment - don't treat as cancelled
            if (checkoutCompletedRef.current) {
                return;
            }
            setViewState(viewStates.PaymentFailed);
            setNotificationMessage(<>
                <LabelMedium>Transaction has been Cancelled.</LabelMedium>
                {(orderSummaryData?.newPlanKey !== "free" && orderSummaryData?.HasNotCompletedTenantSetup == true)
                    ? <LabelMedium>You have been placed on the Free Plan.</LabelMedium>
                    : null}
                <br/>
                If you wish to try again,<br/> please return to the <a href={"/learning-plans"}>Learning
                Plans</a> page.
            </>);
        }
    });

    useEvent({
        eventName: "E186v1_GotTransactionIdForCheckout",
        conversationId: confirmOrderConversationId,
        onEventReceived: (e186) => {

            //* use customer details from backend not frontend to ensure any post-processing and validation has been done

            let customer;
            if (e186.e186_ExistingCustomerId) {
                // For existing customers, still include address details to prefill checkout form
                // This is especially important for trial conversions where billing details need to be collected
                customer = {
                    id: e186.e186_ExistingCustomerId
                };
                // Include address details if provided (for trial conversions and other scenarios)
                if (e186.e186_NewCustomerDetails) {
                    customer.address = {
                        postalCode: e186.e186_NewCustomerDetails.e186_BillingPostcode,
                        countryCode: e186.e186_NewCustomerDetails.e186_BillingCountry
                    };
                }
            } else {
                customer = {
                    email: e186.e186_NewCustomerDetails.e186_Email,
                    address: {
                        postalCode: e186.e186_NewCustomerDetails.e186_BillingPostcode,
                        countryCode: e186.e186_NewCustomerDetails.e186_BillingCountry
                    }
                };
            }

            const checkoutSettings = {
                settings: {
                    allowLogout: false
                    ,
                },
                transactionId: e186.e186_TransactionId,
                customer: customer
            };
            console.warn(checkoutSettings);
            checkoutCompletedRef.current = false;
            Paddle.Checkout.open(checkoutSettings);

            setViewState(viewStates.WaitingForCheckout);
        }
    });
    
    // useTokenRefreshRequired(useCallback((idToken, accessToken) => {
    //     if (viewState === viewStates.OrderDataReceivedWaitingForUserAction || viewState === viewStates.WaitingForOrderData || viewState === viewStates.WaitingForE140) {
    //         console.warn("token refresh due to new account creation.");
    //         (async function () {
    //             await refresh({ignoreCache: true});
    //             setViewState(viewStates.SendingContinueOrder);
    //         })();
    //     }
    // }, [refresh, viewState]));


    const {errors, clearErrors} = useApiErrors((newError, errors) => {

        const errorCodes = {
            pendingCurrencyChange: "601d463e-2558-4a3e-a402-236114bf1709",
            tryingToUpgradeToExistingPlan: "9862dde9-ddcc-475c-bcbf-bcf688a0c82c",
            noApiPermissionForMessage: "67dcd6ad-9b29-4b90-b1aa-2e714af68884" // "This access token is not valid for this message"
        };

        setConfirmOrderConversationId(undefined); //clear ongoing conversation
        setDisabled(true); //disable actions

        // After Auth0 login, JWT can briefly lack API permissions; refresh and retry C169 once via SendingContinueOrder
        if (newError.errorCodes?.includes(errorCodes.noApiPermissionForMessage) && (viewState === viewStates.WaitingForOrderData || viewState === viewStates.WaitingForE140) && permissionErrorRetryCount < 1) {
            setPermissionErrorRetryCount(c => c + 1);
            (async () => {
                await refresh({ignoreCache: true});
                setViewState(viewStates.SendingContinueOrder);
            })();
            clearErrors();
            return;
        }

        if (newError.errorCodes?.includes(errorCodes.pendingCurrencyChange)) {
            setViewState(viewStates.PendingCurrencyChangePreventedOrder);
            //* notification msg would be visible after closing error modal
            setNotificationMessage(<>
                <LabelMedium>Transaction has been cancelled.</LabelMedium>
                If you wish to update your billing details<br/> please visit the <a href={"#/my-profile"}>My
                Profile</a> page.
            </>);
        } else if (newError.errorCodes?.includes(errorCodes.tryingToUpgradeToExistingPlan)) {
            setViewState(viewStates.TryingToUpgradeToExistingPlan);
        }
    });

    useEvent({
        eventName: "E140v1_GotOrderDetailsForConfirmation",
        onEventReceived: (e140) => {

            // Check for quota resolution or unarchive opportunity
            // Handle null values by checking for truthy values instead of strict === true
            if (e140.e140_RequiresQuotaResolution === true) {
                setOrderSummaryData({
                    requiresQuotaResolution: true,
                    newPlanStudentsQuota: e140.e140_NewPlanStudentsQuota,
                    newPlanStudentLoginsQuota: e140.e140_NewPlanStudentLoginsQuota,
                    currentActiveStudentsCount: e140.e140_CurrentActiveStudentsCount,
                    currentStudentLoginsCount: e140.e140_CurrentStudentLoginsCount,
                    quotaResolutionStudents: e140.e140_QuotaResolutionStudents || []
                });
                setViewState(viewStates.QuotaResolutionRequired);
                return;
            }

            if (e140.e140_OffersUnarchiveOpportunity === true) {
                setOrderSummaryData({
                    offersUnarchiveOpportunity: true,
                    newPlanStudentsQuota: e140.e140_NewPlanStudentsQuota,
                    currentActiveStudentsCount: e140.e140_CurrentActiveStudentsCount,
                    unarchiveCandidates: e140.e140_UnarchiveCandidates || []
                });
                setViewState(viewStates.UnarchiveOpportunity);
                return;
            }

            const threeColumns = ['Item', 'Current Plan', 'New Plan'];
            const twoColumns = ['Item', 'Amount'];
            const planChosenIsFreePlan = e140.e140_NewPlanIsFreePlan ?? false;
            const currentPlanIsFreePlan = e140.e140_QueuedPlan?.e140_PlanKey === 'free';
            const taxNotIncluded = "Excl. tax";
            const planChosenIsTrialPlan = e140.e140_NewPlanIsTrialPlan ?? false;
            const isStartingTrial = e140.e140_NewPlan.e140_BillingCodes.includes('is-trial');
            const showCurrentPlan = e140.e140_ShowCurrentPlan ?? true;
            const isOnFreeTrial = (e140.e140_RemainingTrialDays ?? 0) > 0;
            const newPlanTaxMode = planChosenIsFreePlan ? "" : (e140.e140_NewPlan.e140_PlanTaxMode === "external" ? taxNotIncluded : "");
            const currentPlanTaxMode = currentPlanIsFreePlan ? "" : (e140.e140_QueuedPlan?.e140_PlanTaxMode === "external" ? taxNotIncluded : "");
            const data = {
                billingPostcode: e140.e140_BillingPostcode ?? '',
                checkoutNotRequired: e140.e140_NewPlan.e140_BillingCodes.includes('checkout-not-required'),
                isStartingTrial: isStartingTrial,
                billingCodes: e140.e140_NewPlan.e140_BillingCodes,
                profileEmail: e140.e140_ProfileEmail,
                newPlan: e140.e140_NewPlan.e140_PlanName,
                newPlanKey: e140.e140_NewPlan.e140_PlanKey,
                newPlanPrice: e140.e140_NewPlan.e140_PlanPrice,
                newPlanCurrency: e140.e140_NewPlan.e140_PlanCurrency,
                currentPlan: e140.e140_QueuedPlan?.e140_PlanName,
                currentPlanPrice: e140.e140_QueuedPlan?.e140_PlanPrice,
                currentPlanCurrency: e140.e140_QueuedPlan?.e140_PlanCurrency,
                currentPlanKey: e140.e140_QueuedPlan?.e140_PlanKey,
                trialExpiryDate: e140.e140_TrialExpiryDate,
                remainingTrialDays: e140.e140_RemainingTrialDays,
                tableColumns: showCurrentPlan ? threeColumns : twoColumns,
                tableData: showCurrentPlan ?
                    [
                        ["Students", e140.e140_QueuedPlan.e140_PlanCredit.e140_Students, e140.e140_NewPlan.e140_PlanCredit.e140_Students],
                        ["Teacher Logins", e140.e140_QueuedPlan.e140_PlanCredit.e140_TeacherLogins, e140.e140_NewPlan.e140_PlanCredit.e140_TeacherLogins],
                        ["Student Logins", e140.e140_QueuedPlan.e140_PlanCredit.e140_StudentLogins, e140.e140_NewPlan.e140_PlanCredit.e140_StudentLogins],
                        ["Assessments", e140.e140_QueuedPlan.e140_PlanCredit.e140_Assessments, e140.e140_NewPlan.e140_PlanCredit.e140_Assessments],
                        ["Lessons and Activities", e140.e140_QueuedPlan.e140_PlanCredit.e140_LessonsAndActivities, e140.e140_NewPlan.e140_PlanCredit.e140_LessonsAndActivities],
                        ["Price", <LabelLarge
                            key={uuidv4()}>{isOnFreeTrial ? "Free" : toMoney(e140.e140_QueuedPlan.e140_PlanPrice, e140.e140_QueuedPlan.e140_PlanCurrency)}
                            <LabelSmall>{currentPlanTaxMode}</LabelSmall></LabelLarge>,
                            <LabelLarge
                                key={uuidv4()}>{toMoney(e140.e140_NewPlan.e140_PlanPrice, e140.e140_NewPlan.e140_PlanCurrency)}
                                <LabelSmall>{newPlanTaxMode}</LabelSmall></LabelLarge>],
                    ]
                    : [
                        ["Students", e140.e140_NewPlan.e140_PlanCredit.e140_Students],
                        ["Teacher Logins", e140.e140_NewPlan.e140_PlanCredit.e140_TeacherLogins],
                        ["Student Logins", e140.e140_NewPlan.e140_PlanCredit.e140_StudentLogins],
                        ["Assessments", e140.e140_NewPlan.e140_PlanCredit.e140_Assessments],
                        ["Lessons and Activities", e140.e140_NewPlan.e140_PlanCredit.e140_LessonsAndActivities],
                        ["Price", <LabelLarge
                            key={uuidv4()}>{toMoney(e140.e140_NewPlan.e140_PlanPrice, e140.e140_NewPlan.e140_PlanCurrency)}
                            <LabelSmall>{newPlanTaxMode}</LabelSmall></LabelLarge>],
                    ]
            };

            setOrderSummaryData(data);
            setFirstName(e140.e140_ProfileFirstName);
            setLastName(e140.e140_ProfileLastName ?? '');
            setPostcode(e140.e140_BillingPostcode ?? '');
            setCountryCode(e140.e140_BillingCountry.selectedKeys[0]);
            setCountries(e140.e140_BillingCountry.allEnumerations);
            setCurrencyCode(e140.e140_BillingCurrency.selectedKeys[0]);
            setCurrencies(e140.e140_BillingCurrency.allEnumerations);
            setViewState(viewStates.OrderDataReceivedWaitingForUserAction);

            setConfirmOrderConversationId(undefined);
            setDisabled(false);
        }
    });


    useEvent({
        eventName: "E143v1_OrderCompleted",
        conversationId: confirmOrderConversationId,
        onEventReceived: (e143) => {

            const columnsPlanOnly = ['Item', 'Amount'];
            const newPlanData = [
                ["Students", e143.e143_Credit.e143_Students],
                ["Teacher Logins", e143.e143_Credit.e143_TeacherLogins],
                ["Student Logins", e143.e143_Credit.e143_StudentLogins],
                ["Assessments", e143.e143_Credit.e143_Assessments],
                ["Lessons and Activities", e143.e143_Credit.e143_LessonsAndActivities]
            ];
            const data = {
                planKey: e143.e143_PlanKey,
                name: e143.e143_PlanName,
                tableColumns: columnsPlanOnly,
                tableData: newPlanData,
                isNewUser: e143.e143_IsNewUser ?? false
            };

            setOrderCompletedData(data);
            setViewState(viewStates.PaymentConfirmed);
        }
    });

    useEffect(() => {
        if (viewState === viewStates.PaymentConfirmed && orderCompletedData) {
            const itemId = uuidv4();
            sessionStorage.setItem(itemId, JSON.stringify(orderCompletedData));
            sessionStorage.removeItem("oc-loaded-once");
            location.href = '#/order-complete/' + itemId;
        }
    }, [viewState, orderCompletedData]);

    async function sendConfirmCommand(refreshOnly, currencyCode, countryCode, postcode, firstName, lastName) {
        // Check if billing details are required (trials don't need them)
        const requireBillingDetails = orderSummaryData?.billingCodes?.includes('require-billing-details') ?? false;
        
        const confirmOrderCommand = {
            $type: toTypeName("C171v1_ConfirmOrder"),
            headers: [{
                key: headerKeys.statefulProcessId,
                value: statefulProcessIdAkaOrderId
            }],
            C171_FirstName: firstName,
            C171_LastName: lastName,
            C171_AllowChangeOfBillingDetails: requireBillingDetails,
            C171_RefreshOnly: refreshOnly,
            // Explicitly set billing fields - null when not required, actual values when required
            C171_BillingPostcode: requireBillingDetails ? postcode : null,
            C171_BillingCountryCode: requireBillingDetails ? countryCode : null,
            C171_BillingCurrencyCode: requireBillingDetails ? currencyCode : null
        };
        const messages = await validateCommand(confirmOrderCommand);

        if (messages) {
            setNotificationMessage(messages.map((m, i) => <p key={i}>{m}</p>));
        } else {
            setNotificationMessage(undefined);
            const conversationId = confirmOrderTrigger(confirmOrderCommand);  //* still send this as it still returns e143

            if (orderSummaryData.checkoutNotRequired && !refreshOnly) {
                setViewState(viewStates.PaymentNotRequired);
            } else { //wait for transactionId
                setConfirmOrderConversationId(conversationId); //this will be used to disable the checkout button while processing
                setDisabled(true);
            }
        }
    }

    const [cancelTrigger] = useLazyCommand('C168v1_CancelOrder');
    const [confirmOrderTrigger] = useLazyCommand('C171v1_ConfirmOrder');

    function handleCancel() {
        cancelTrigger({
            headers: [{
                key: headerKeys.statefulProcessId,
                value: statefulProcessIdAkaOrderId
            }]
        });
        location.assign(location.origin + '/learning-plans');
    }

    function renderQuotaResolutionScreen() {
        const data = orderSummaryData;
        const students = data.quotaResolutionStudents || [];

        const updateResolution = (studentId, field, value) => {
            setResolutionChoices(prev => {
                const updated = {
                    ...prev,
                    [studentId]: {
                        ...prev[studentId],
                        [field]: value
                    }
                };
                
                // If archive is checked, automatically check and disable removeLogin
                if (field === 'archive' && value === true) {
                    updated[studentId].removeLogin = true;
                }
                
                return updated;
            });
        };

        // Calculate counts after resolutions (only active students are in the table)
        const activeAfterArchive = students.filter(s => 
            !resolutionChoices[s.e140_StudentId]?.archive
        ).length;

        const loginsAfterRemoval = students.filter(s => 
            s.e140_HasLogin && 
            !resolutionChoices[s.e140_StudentId]?.removeLogin &&
            !resolutionChoices[s.e140_StudentId]?.archive
        ).length;

        const canProceed = activeAfterArchive <= (data.newPlanStudentsQuota || 0) &&
                          loginsAfterRemoval <= (data.newPlanStudentLoginsQuota || 0);

        const handleProceed = async () => {
            const resolutions = students.map(s => ({
                C294_StudentId: s.e140_StudentId,
                C294_Archive: resolutionChoices[s.e140_StudentId]?.archive || false,
                C294_RemoveLogin: resolutionChoices[s.e140_StudentId]?.removeLogin || false
            }));

            const command = {
                $type: toTypeName("C294v1_ResolveOrderQuota"),
                headers: [{
                    key: headerKeys.statefulProcessId,
                    value: statefulProcessIdAkaOrderId
                }],
                C294_Resolutions: resolutions
            };

            const messages = await validateCommand(command);
            if (messages) {
                setNotificationMessage(messages.map((m, i) => <p key={i}>{m}</p>));
            } else {
                setNotificationMessage(undefined);
                resolveQuotaTrigger(command);
            }
        };

        return (
            <Frame>
                <CenteredCard title="Plan Change Required">
                    <SpaceAround>
                        <Grid gridMargins={0} gridGaps={5}>
                            <Cell span={12}>
                                <LabelLarge>Your new plan allows fewer students and/or student logins than you currently use.</LabelLarge>
                                <ParagraphMedium style={{marginTop: '12px'}}>
                                    You'll need to archive some students and/or remove logins to continue.
                                    {data.newPlanStudentsQuota !== null && data.currentActiveStudentsCount !== null && (
                                        <span> You currently have {data.currentActiveStudentsCount} active students, but the new plan allows {data.newPlanStudentsQuota}.</span>
                                    )}
                                    {data.newPlanStudentLoginsQuota !== null && data.currentStudentLoginsCount !== null && (
                                        <span> You currently have {data.currentStudentLoginsCount} student logins, but the new plan allows {data.newPlanStudentLoginsQuota}.</span>
                                    )}
                                </ParagraphMedium>
                                <TalkBadge showRobot={false} kind="warning" style={{marginTop: '12px'}}>
                                    Once you confirm this plan change, you will immediately be bound by the new plan's limits for adding students and student logins, even though billing changes at your next renewal date.
                                </TalkBadge>
                            </Cell>
                            <Cell span={12}>
                                <SpaceAround>
                                    <PrimaryActionMenuButton className={css({backgroundColor: 'grey'})} onClick={handleCancel}>
                                        Cancel
                                    </PrimaryActionMenuButton>
                                    <PrimaryActionMenuButton 
                                        onClick={() => setResolutionPanelExpanded(true)}
                                        style={{marginLeft: "20px"}}
                                    >
                                        Continue
                                    </PrimaryActionMenuButton>
                                </SpaceAround>
                            </Cell>
                            <Cell span={12}>
                                <div style={{
                                    marginTop: '24px', 
                                    padding: '16px', 
                                    border: '1px solid #ccc', 
                                    borderRadius: '4px',
                                    display: resolutionPanelExpanded ? 'block' : 'none'
                                }}>
                                    <LabelMedium style={{marginBottom: '12px'}}>
                                        Active students: {activeAfterArchive} / {data.newPlanStudentsQuota || 0}
                                    </LabelMedium>
                                    <LabelMedium style={{marginBottom: '12px'}}>
                                        Student logins: {loginsAfterRemoval} / {data.newPlanStudentLoginsQuota || 0}
                                    </LabelMedium>
                                    <Table 
                                        columns={['Name', 'Has Login', 'Is Archived']}
                                        data={students.map(s => {
                                            const willArchive = resolutionChoices[s.e140_StudentId]?.archive || false;
                                            const willRemoveLogin = resolutionChoices[s.e140_StudentId]?.removeLogin || false;
                                            const hasLogin = s.e140_HasLogin;
                                            
                                            return [
                                                s.e140_DisplayName,
                                                hasLogin ? (
                                                    <input
                                                        key={`login-${s.e140_StudentId}`}
                                                        type="checkbox"
                                                        checked={!willRemoveLogin}
                                                        disabled={willArchive || !hasLogin}
                                                        onChange={e => updateResolution(s.e140_StudentId, 'removeLogin', !e.target.checked)}
                                                    />
                                                ) : 'No',
                                                <input
                                                    key={`archive-${s.e140_StudentId}`}
                                                    type="checkbox"
                                                    checked={willArchive}
                                                    onChange={e => updateResolution(s.e140_StudentId, 'archive', e.target.checked)}
                                                />
                                            ];
                                        })}
                                    />
                                    <SpaceTop>
                                        <SpaceAround>
                                            <PrimaryActionMenuButton className={css({backgroundColor: 'grey'})} onClick={handleCancel}>
                                                Cancel
                                            </PrimaryActionMenuButton>
                                            <PrimaryActionMenuButton 
                                                onClick={handleProceed}
                                                disabled={!canProceed}
                                                style={{marginLeft: "20px"}}
                                            >
                                                Proceed
                                            </PrimaryActionMenuButton>
                                        </SpaceAround>
                                    </SpaceTop>
                                </div>
                            </Cell>
                            <Cell span={12}>
                                <ValidationNotification errorMsg={notificationMessage}/>
                            </Cell>
                        </Grid>
                    </SpaceAround>
                </CenteredCard>
            </Frame>
        );
    }

    function renderUnarchiveScreen() {
        const data = orderSummaryData;
        const candidates = data.unarchiveCandidates || [];
        const maxCanUnarchive = (data.newPlanStudentsQuota || 0) - (data.currentActiveStudentsCount || 0);

        const handleProceed = async () => {
            const command = {
                $type: toTypeName("C295v1_UnarchiveStudentsForOrder"),
                headers: [{
                    key: headerKeys.statefulProcessId,
                    value: statefulProcessIdAkaOrderId
                }],
                C295_StudentIds: selectedUnarchiveIds
            };

            const messages = await validateCommand(command);
            if (messages) {
                setNotificationMessage(messages.map((m, i) => <p key={i}>{m}</p>));
            } else {
                setNotificationMessage(undefined);
                unarchiveStudentsTrigger(command);
            }
        };

        const handleSkip = async () => {
            const command = {
                $type: toTypeName("C295v1_UnarchiveStudentsForOrder"),
                headers: [{
                    key: headerKeys.statefulProcessId,
                    value: statefulProcessIdAkaOrderId
                }],
                C295_StudentIds: []
            };
            unarchiveStudentsTrigger(command);
        };

        return (
            <Frame>
                <CenteredCard title="Unarchive Students">
                    <SpaceAround>
                        <Grid gridMargins={0} gridGaps={5}>
                            <Cell span={12}>
                                <LabelLarge>Your new plan allows more students.</LabelLarge>
                                <ParagraphMedium style={{marginTop: '12px'}}>
                                    You can unarchive some of your archived students now.
                                    {maxCanUnarchive > 0 && (
                                        <span> You may unarchive up to {maxCanUnarchive} students.</span>
                                    )}
                                </ParagraphMedium>
                            </Cell>
                            <Cell span={12}>
                                <SpaceAround>
                                    <PrimaryActionMenuButton className={css({backgroundColor: 'grey'})} onClick={handleCancel}>
                                        Cancel
                                    </PrimaryActionMenuButton>
                                    <PrimaryActionMenuButton 
                                        onClick={() => setUnarchivePanelExpanded(true)}
                                        style={{marginLeft: "20px"}}
                                    >
                                        Continue
                                    </PrimaryActionMenuButton>
                                </SpaceAround>
                            </Cell>
                            <Cell span={12}>
                                <div style={{
                                    marginTop: '24px', 
                                    padding: '16px', 
                                    border: '1px solid #ccc', 
                                    borderRadius: '4px',
                                    display: unarchivePanelExpanded ? 'block' : 'none'
                                }}>
                                    {maxCanUnarchive > 0 && (
                                        <LabelMedium style={{marginBottom: '12px'}}>
                                            You may unarchive up to {maxCanUnarchive} students
                                        </LabelMedium>
                                    )}
                                    <Table 
                                        columns={['Name', 'Unarchive']}
                                        data={candidates.map(c => [
                                            c.e140_DisplayName,
                                            <input
                                                key={`unarchive-${c.e140_UnarchiveStudentId}`}
                                                type="checkbox"
                                                checked={selectedUnarchiveIds.includes(c.e140_UnarchiveStudentId)}
                                                disabled={!selectedUnarchiveIds.includes(c.e140_UnarchiveStudentId) && selectedUnarchiveIds.length >= maxCanUnarchive}
                                                onChange={e => {
                                                    if (e.target.checked) {
                                                        setSelectedUnarchiveIds([...selectedUnarchiveIds, c.e140_UnarchiveStudentId]);
                                                    } else {
                                                        setSelectedUnarchiveIds(selectedUnarchiveIds.filter(id => id !== c.e140_UnarchiveStudentId));
                                                    }
                                                }}
                                            />
                                        ])}
                                    />
                                    <SpaceTop>
                                        <SpaceAround>
                                            <PrimaryActionMenuButton className={css({backgroundColor: 'grey'})} onClick={handleCancel}>
                                                Cancel
                                            </PrimaryActionMenuButton>
                                            <PrimaryActionMenuButton 
                                                onClick={handleSkip}
                                                style={{marginLeft: "20px"}}
                                            >
                                                Skip
                                            </PrimaryActionMenuButton>
                                            <PrimaryActionMenuButton 
                                                onClick={handleProceed}
                                                disabled={selectedUnarchiveIds.length > maxCanUnarchive}
                                                style={{marginLeft: "20px"}}
                                            >
                                                Proceed
                                            </PrimaryActionMenuButton>
                                        </SpaceAround>
                                    </SpaceTop>
                                </div>
                            </Cell>
                            <Cell span={12}>
                                <ValidationNotification errorMsg={notificationMessage}/>
                            </Cell>
                        </Grid>
                    </SpaceAround>
                </CenteredCard>
            </Frame>
        );
    }

    switch (viewState) {
        case viewStates.PaymentAcceptedButNotConfirmed:
            return (<Frame>
                <CenteredCard
                    title={<div style={{
                        width: "100%"
                    }}><Centered>Processing your payment</Centered>
                        <Centered>and updating your account.</Centered>
                        <SpaceTop>
                            <SpaceTopAndBottom>
                                <Centered><ParagraphMedium>This could take up to 90
                                    seconds.</ParagraphMedium></Centered>
                            </SpaceTopAndBottom>
                            <Centered centerText={true}><ParagraphMedium>You can wait for confirmation or you can
                                continue using the app
                                and your credit will be added in the next few minutes.</ParagraphMedium></Centered>
                        </SpaceTop>
                        <SpaceTop>
                            <CenterSpinner/>
                        </SpaceTop>
                    </div>} showRobot={false}>
                </CenteredCard>
            </Frame>);
        case viewStates.PaymentNotRequired:
            return (<Frame>
                <CenteredCard
                    title={<div style={{
                        width: "100%"
                    }}><Centered>Updating your account</Centered>
                        <SpaceTop>
                            <SpaceTopAndBottom>
                                <Centered><ParagraphMedium>This could take up to 90
                                    seconds.</ParagraphMedium></Centered>
                            </SpaceTopAndBottom>
                            <Centered centerText={true}><ParagraphMedium>You can wait for confirmation or you can
                                continue using the app
                                and your credit will be added in the next few minutes.</ParagraphMedium></Centered>
                        </SpaceTop>
                        <SpaceTop>
                            <CenterSpinner/>
                        </SpaceTop>
                    </div>} showRobot={false}>
                </CenteredCard>
            </Frame>);
        case viewStates.PaymentFailed:
            return (
                <Frame><CenteredCard><ValidationNotification errorMsg={notificationMessage}/></CenteredCard></Frame>);
        case viewStates.PendingCurrencyChangePreventedOrder:
            return (
                <Frame>
                    <ErrorModal onClose={() => setViewState(viewStates.PaymentFailed)}>
                        <>
                            <TalkBadge><p>{translate("$CannotChangeSubscriptionWithPendingCurrencyChange")}<br/></p>
                            </TalkBadge>
                        </>
                    </ErrorModal>
                </Frame>);
        case viewStates.TryingToUpgradeToExistingPlan:
            return (
                <Frame>
                    <ErrorModal onClose={() => {
                        location.href = "/learning-plans";
                    }}>
                        <>
                            <TalkBadge><p>{translate("$CannotUpgradeToExistingPlan")}<br/></p></TalkBadge>
                        </>
                    </ErrorModal>
                </Frame>
            );
        case viewStates.PaymentConfirmed:
            return <Frame/>;
        case viewStates.WaitingForOrderData:
        case viewStates.SendingContinueOrder:
        case viewStates.WaitingForE140:
            return (<Frame><CenteredCard><CenterSpinner/></CenteredCard></Frame>);
        case viewStates.WaitingForCheckout:
            return (<Frame>
                <CenteredCard
                    title={<div style={{
                        width: "100%",
                        textAlign: "center"
                    }}>{`You chose, ${orderSummaryData.newPlan}`}</div>}>
                </CenteredCard>
            </Frame>);
        case viewStates.QuotaResolutionRequired:
            return renderQuotaResolutionScreen();
        case viewStates.UnarchiveOpportunity:
            return renderUnarchiveScreen();
        case viewStates.OrderDataReceivedWaitingForUserAction:
            let billingInfo = null;

            if (orderSummaryData.billingCodes.includes('trial-conversion')) {
                // Trial user converting to paid plan
                const trialExpiryDate = orderSummaryData.trialExpiryDate ? formatDate(orderSummaryData.trialExpiryDate) : 'the end of your trial';
                const remainingDays = orderSummaryData.remainingTrialDays || 0;
                billingInfo = (<>
                    <SpaceBottom> <LabelLarge>Trial Conversion</LabelLarge></SpaceBottom>
                    <TalkBadge showRobot={true}>Your trial will continue until {trialExpiryDate}</TalkBadge>
                    <div style={{marginTop: '12px'}}>
                        <strong>Billing starts:</strong> {trialExpiryDate}<br/>
                        <strong>First charge:</strong> {toMoney(orderSummaryData.newPlanPrice, orderSummaryData.newPlanCurrency)} on {trialExpiryDate}<br/>
                        <strong>Trial remaining:</strong> {remainingDays} days
                    </div>
                </>);
            } else if (orderSummaryData.billingCodes.includes('trial-to-custom-immediate')) {
                // Trial user switching to custom plan - ends trial immediately
                billingInfo = (<>
                    <SpaceBottom> <LabelLarge>Immediate Activation</LabelLarge></SpaceBottom>
                    <TalkBadge showRobot={true} kind="warning">
                        Switching to a custom plan will end your trial immediately.
                    </TalkBadge>
                    <div style={{marginTop: '12px', padding: '12px', backgroundColor: '#fff3cd', borderRadius: '4px'}}>
                        <strong>This will:</strong>
                        <ul style={{marginTop: '8px', paddingLeft: '20px'}}>
                            <li>End your current trial</li>
                            <li>Start billing immediately</li>
                            <li>Apply custom plan limits right away</li>
                        </ul>
                    </div>
                </>);
            } else if (orderSummaryData.billingCodes.includes('bill-immediately')) {
                billingInfo = (<>
                    <TalkBadge showRobot={true}>This change in plans will take effect immediately.
                        You will be credited for any unused days remaining on the old plan and billed for any
                        outstanding balance on the new plan immediately.</TalkBadge>
                </>);
            } else if (orderSummaryData.billingCodes.includes("revert-to-free")) {
                billingInfo = (<><SpaceBottom> <LabelLarge>Billing</LabelLarge></SpaceBottom>
                    <TalkBadge showRobot={true}>Reverting to the Freebie will not cause the loss of any of your
                        data.</TalkBadge>
                    You will retain access to the credit in your old plan until your next renewal date at which time
                    your credit will be downgraded.
                    While your data is retained, you may lose access to some features if reverting to the free plan
                    causes you to exceed free plan limits.
                </>);
            } else if (orderSummaryData.billingCodes.includes('bill-at-renewal')) {
                // Check if this is a downgrade by comparing credits (Students or Student Logins reduced)
                const queuedPlanStudents = orderSummaryData.tableData?.[0]?.[1];
                const newPlanStudents = orderSummaryData.tableData?.[0]?.[2];
                const queuedPlanStudentLogins = orderSummaryData.tableData?.[2]?.[1];
                const newPlanStudentLogins = orderSummaryData.tableData?.[2]?.[2];
                const isDowngrade = (queuedPlanStudents && newPlanStudents && Number(queuedPlanStudents) > Number(newPlanStudents)) ||
                                   (queuedPlanStudentLogins && newPlanStudentLogins && Number(queuedPlanStudentLogins) > Number(newPlanStudentLogins));
                
                billingInfo = (<>
                    <TalkBadge showRobot={true}>Your new plan will begin on your renewal date.</TalkBadge>
                    {isDowngrade && (
                        <TalkBadge showRobot={false} kind="warning" style={{marginTop: '12px'}}>
                            Important: Once you confirm this plan change, you will immediately be bound by the new plan's limits for adding students and student logins, even though billing changes at your next renewal date.
                        </TalkBadge>
                    )}
                </>);
            } else if (orderSummaryData.isStartingTrial 
                       && !orderSummaryData.billingCodes.includes('trial-conversion')
                       && orderSummaryData.trialExpiryDate) {
                // New trial user (not converting) - show trial expiry message
                const trialExpiryDate = formatDate(orderSummaryData.trialExpiryDate);
                billingInfo = (<>
                    <TalkBadge showRobot={true}>Your trial will continue until {trialExpiryDate}</TalkBadge>
                </>);
            }

            const confirmButtonText = orderSummaryData.checkoutNotRequired ? "Confirm" : (disabled ?
                <CenterSpinner/> : "Proceed To Payment");

            return (
                <Frame>
                    <CenteredCard
                        title={<div style={{
                            width: "100%",
                            textAlign: "center"
                        }}>{`You chose, ${orderSummaryData.newPlan}`}</div>}>
                        <SpaceAround>
                            <Grid gridMargins={0} gridGaps={5}>
                                <Cell span={[1, 3, 5]}>
                                    <div className={css({paddingTop: theme.sizing.scale200})}>First Name</div>
                                </Cell>
                                <Cell span={[3, 5, 7]}><Input value={firstName}
                                                              onChange={e => setFirstName(e.target.value)}/></Cell>
                                <Cell span={[1, 3, 5]}>
                                    <div className={css({paddingTop: theme.sizing.scale200})}>Last Name</div>
                                </Cell>
                                <Cell span={[3, 5, 7]}><Input value={lastName}
                                                              onChange={e => setLastName(e.target.value)}/></Cell>
                                {orderSummaryData.billingCodes.includes('require-billing-details') ? [
                                        //* CURRENCY
                                        ...(orderSummaryData.isStartingTrial ? [] : [
                                            <Cell key={0} span={12}><TalkBadge
                                                showRobot={true}>{orderSummaryData.checkoutNotRequired && orderSummaryData.currentPlanCurrency && orderSummaryData.currentPlanCurrency !== orderSummaryData.newPlanCurrency ? "Currency changes will not take effect until the new renewal date. Please ensure the Billing Address matches the one registered with your payment provider." : "Purchases can be made in any currency. Please ensure the Billing Address matches the one registered with your payment provider."} {
                                                }</TalkBadge></Cell>
                                        ]),
                                        <Cell key={1} span={[1, 3, 5]}>
                                            <div className={css({paddingTop: theme.sizing.scale200})}>Billing Currency</div>
                                        </Cell>,
                                        <Cell key={2} span={[3, 5, 7]}><Select
                                            options={currencies}
                                            backspaceRemoves={false}
                                            clearable={false}
                                            deleteRemoves={false}
                                            escapeClearsValue={false}
                                            required={true}
                                            searchable={false}
                                            value={[{key: currencyCode}]}
                                            labelKey="value"
                                            valueKey="key"
                                            disabled={disabled}
                                            onChange={params => {
                                                const currencyCode = params?.value[0]?.key;
                                                setCurrencyCode(currencyCode);
                                                sendConfirmCommand(true, currencyCode, countryCode, postcode, firstName, lastName);
                                            }}
                                        /></Cell>,

                                        //* BILLING POSTCODE
                                        <Cell key={3} span={[1, 3, 5]}>
                                            <div className={css({paddingTop: theme.sizing.scale200})}>Billing Zip/Post
                                                Code
                                            </div>
                                        </Cell>,
                                        <Cell key={4} span={[3, 5, 7]}><Input value={postcode} required={true}
                                                                              disabled={disabled}
                                                                              onChange={e => setPostcode(e.target.value)}/></Cell>,
                                        //* BILLING COUNTRY
                                        <Cell key={5} span={[1, 3, 5]}>
                                            <div className={css({paddingTop: theme.sizing.scale200})}>Billing Country</div>
                                        </Cell>,
                                        <Cell key={6} span={[3, 5, 7]}><Select
                                            disabled={disabled}
                                            options={countries}
                                            backspaceRemoves={false}
                                            clearable={false}
                                            deleteRemoves={false}
                                            escapeClearsValue={false}
                                            required={true}
                                            searchable={false}
                                            value={[{key: countryCode}]}
                                            labelKey="value"
                                            valueKey="key"
                                            onChange={params => {
                                                const countryCode = params?.value[0]?.key;
                                                setCountryCode(countryCode);
                                                sendConfirmCommand(true, currencyCode, countryCode, postcode, firstName, lastName);
                                            }}
                                        /></Cell>]
                                    : null}
                                <Cell span={12}>
                                    <SpaceTop override={theme.sizing.scale600}>
                                        <LabelLarge>Plan Details</LabelLarge>
                                    </SpaceTop>
                                </Cell>
                                <Cell span={12}>
                                    <ValidationNotification errorMsg={notificationMessage}/>
                                </Cell>
                                <Cell span={12}>
                                    <Table columns={orderSummaryData.tableColumns} data={orderSummaryData.tableData}/>
                                </Cell>
                                <Cell span={12}>
                                    <SpaceBottom>
                                        {billingInfo}
                                    </SpaceBottom>
                                    <SpaceAround>
                                        <div className={css({textAlign: 'end'})}>
                                            <PrimaryActionMenuButton className={css({backgroundColor: 'grey'})}
                                                                     onClick={() => {
                                                                         cancelTrigger({
                                                                             headers: [{
                                                                                 key: headerKeys.statefulProcessId,
                                                                                 value: statefulProcessIdAkaOrderId
                                                                             }]
                                                                         });
                                                                         location.assign(location.origin + '/learning-plans');
                                                                     }}>Back</PrimaryActionMenuButton>
                                            <PrimaryActionMenuButton style={{marginLeft: "20px"}}
                                                                     onClick={() => sendConfirmCommand(false, currencyCode, countryCode, postcode, firstName, lastName)}
                                                                     disabled={disabled}
                                            >
                                                {confirmButtonText}</PrimaryActionMenuButton>
                                        </div>
                                    </SpaceAround>
                                </Cell>
                            </Grid>
                        </SpaceAround>
                    </CenteredCard>
                </Frame>
            );


    }
}